import React from 'react';
import { WORLD_PATHS, WORLD_VIEWBOX } from './world-map-paths';

/**
 * CashelBlueExportMap
 * --------------------
 * Editorial-style world map showing Cashel Blue's international distribution footprint.
 *
 * Uses the public-domain `simple-world-map` (CC BY-SA 3.0, Al MacDonald /
 * Fritz Lekschas) as the geographic base layer, with stylised brand-tuned
 * markers and great-circle arcs from Tipperary, Ireland to each export
 * destination.
 *
 *   variant="dark"  → navy background (default, for site)
 *   variant="light" → cream background (for print PDFs)
 *   compact         → trim margins for inline use
 */

// All coords below are in the world-map-paths SVG coordinate system
// (Robinson-like projection, viewBox = "30.767 241.591 784.077 458.627").
// Hand-calibrated against the actual country path bounding boxes.

const ORIGIN = { name: 'Tipperary, Ireland', x: 391, y: 379 };

const MARKETS = [
  // Coords approximate centroid of each country in the world-map SVG
  { id: 'uk',  name: 'United Kingdom', sub: 'England · Scotland', x: 405, y: 374, labelOffset: [-12, -8],  labelAnchor: 'end'   },
  { id: 'fr',  name: 'France',         sub: 'Paris',              x: 420, y: 405, labelOffset: [-10, 18],  labelAnchor: 'end'   },
  { id: 'bnl', name: 'Benelux',        sub: 'Belgium · Netherlands', x: 421, y: 388, labelOffset: [16, 26], labelAnchor: 'start' },
  { id: 'dk',  name: 'Denmark',        sub: 'Copenhagen',         x: 432, y: 374, labelOffset: [12, -10],  labelAnchor: 'start' },
  { id: 'de',  name: 'Germany',        sub: 'Berlin',             x: 432, y: 390, labelOffset: [16, 8],    labelAnchor: 'start' },
  { id: 'us',  name: 'USA',            sub: 'New York',           x: 230, y: 412, labelOffset: [-10, 10],  labelAnchor: 'end'   },
  { id: 'ca',  name: 'Canada',         sub: 'Toronto',            x: 220, y: 395, labelOffset: [-10, -8],  labelAnchor: 'end'   },
  { id: 'me',  name: 'Middle East',    sub: 'Dubai · Riyadh',     x: 528, y: 468, labelOffset: [12, 4],    labelAnchor: 'start' },
  { id: 'au',  name: 'Australia',      sub: 'Sydney',             x: 750, y: 622, labelOffset: [-12, 8],   labelAnchor: 'end'   },
];

const buildArc = (a, b) => {
  // Smooth quadratic curve, sagging up (smaller y) for a nice arc feel
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - Math.abs(b.x - a.x) * 0.15 - 12;
  return `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${mx.toFixed(1)} ${my.toFixed(1)}, ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
};

export default function CashelBlueExportMap({
  variant = 'dark',
  compact = false,
  showTitle = true,
  showLegend = true,
}) {
  const isDark = variant === 'dark';
  const palette = isDark
    ? {
        bg: '#0A2540',
        landFill: 'rgba(255, 255, 255, 0.08)',
        landStroke: 'rgba(255, 255, 255, 0.18)',
        arc: 'rgba(201, 169, 97, 0.65)',
        origin: '#E91E63',
        originGlow: 'rgba(233, 30, 99, 0.30)',
        marker: '#C9A961',
        markerGlow: 'rgba(201, 169, 97, 0.30)',
        label: '#F1F5F9',
        labelMuted: '#94A3B8',
        title: '#fff',
      }
    : {
        bg: '#FAF8F5',
        landFill: 'rgba(10, 37, 64, 0.10)',
        landStroke: 'rgba(10, 37, 64, 0.22)',
        arc: 'rgba(10, 37, 64, 0.45)',
        origin: '#E91E63',
        originGlow: 'rgba(233, 30, 99, 0.22)',
        marker: '#0A2540',
        markerGlow: 'rgba(10, 37, 64, 0.18)',
        label: '#1A2332',
        labelMuted: '#4A5568',
        title: '#0A2540',
      };

  // Crop the world-map viewBox to focus on inhabited continents (drop Antarctica + far poles)
  // Original: "30.767 241.591 784.077 458.627"  → x∈[30.8, 814.8], y∈[241.6, 700.2]
  const VIEW = compact ? '110 290 705 400' : '95 280 720 420';

  return (
    <div data-testid="cashel-blue-export-map" style={{
      background: palette.bg,
      borderRadius: 10,
      padding: compact ? '20px 18px' : '32px 28px 24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: isDark
        ? '0 12px 40px rgba(10,37,64,0.25), inset 0 1px 0 rgba(255,255,255,0.06)'
        : '0 8px 24px rgba(10,37,64,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
    }}>
      {showTitle && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 16,
          paddingBottom: 14,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,37,64,0.10)'}`,
        }}>
          <div>
            <div style={{
              fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
              fontWeight: 700, color: palette.origin, marginBottom: 6,
            }}>
              Cashel Blue · International scale
            </div>
            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(20px, 2.4vw, 26px)',
              fontWeight: 700, color: palette.title,
              letterSpacing: '-0.02em', lineHeight: 1.15,
            }}>
              From a Tipperary creamery<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: palette.origin }}>to international markets.</em>
            </div>
          </div>
          <div style={{
            display: 'flex', gap: 22, alignItems: 'flex-end',
            color: palette.labelMuted, fontSize: 12,
          }}>
            <Stat label="Internal SKUs" value="40+" palette={palette} />
            <Stat label="Export markets" value="9" palette={palette} />
            <Stat label="Customer-mapped" value="∞" palette={palette} hint="every customer · own private-label" />
          </div>
        </div>
      )}

      <svg
        viewBox={VIEW}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        role="img"
        aria-label="World map showing Cashel Blue's international distribution footprint"
      >
        <defs>
          <filter id="cb-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <style>{`
            .cb-land path { fill: ${palette.landFill}; stroke: ${palette.landStroke}; stroke-width: 0.4; }
          `}</style>
        </defs>

        {/* Geographic base layer */}
        <g
          className="cb-land"
          dangerouslySetInnerHTML={{ __html: WORLD_PATHS }}
        />

        {/* Hairline arcs from Ireland to each market */}
        <g fill="none" stroke={palette.arc} strokeWidth="0.7" strokeLinecap="round">
          {MARKETS.map((m) => (
            <path
              key={`arc-${m.id}`}
              d={buildArc(ORIGIN, m)}
              opacity="0.85"
              strokeDasharray="2 2"
            />
          ))}
        </g>

        {/* Origin: Tipperary (animated pulse) */}
        <g>
          <circle cx={ORIGIN.x} cy={ORIGIN.y} r="9" fill={palette.originGlow}>
            <animate attributeName="r" values="7;13;7" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.55;0.10;0.55" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx={ORIGIN.x} cy={ORIGIN.y} r="3.6" fill={palette.origin} filter="url(#cb-glow)" />
          <circle cx={ORIGIN.x} cy={ORIGIN.y} r="1.2" fill="#fff" />
          <line
            x1={ORIGIN.x - 4}
            y1={ORIGIN.y + 2}
            x2={ORIGIN.x - 14}
            y2={ORIGIN.y + 14}
            stroke={palette.origin}
            strokeWidth="0.5"
            opacity="0.75"
          />
          <text
            x={ORIGIN.x - 16}
            y={ORIGIN.y + 17}
            textAnchor="end"
            fontSize="7.5"
            fontWeight="700"
            fill={palette.origin}
            style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}
          >
            Tipperary
          </text>
          <text
            x={ORIGIN.x - 16}
            y={ORIGIN.y + 25}
            textAnchor="end"
            fontSize="6"
            fill={palette.label}
            style={{ letterSpacing: '0.04em' }}
          >
            Origin · Ireland
          </text>
        </g>

        {/* Destination markers + labels with leader lines */}
        <g>
          {MARKETS.map((m) => {
            const [dx, dy] = m.labelOffset;
            const labelX = m.x + dx;
            const labelY = m.y + dy;
            const drawLeader = Math.abs(dx) > 6 || Math.abs(dy) > 6;
            const leaderEndX = m.labelAnchor === 'end' ? labelX + 1 : labelX - 1;
            return (
              <g key={m.id}>
                {drawLeader && (
                  <line
                    x1={m.x}
                    y1={m.y}
                    x2={leaderEndX}
                    y2={labelY - 1}
                    stroke={palette.labelMuted}
                    strokeWidth="0.35"
                    opacity="0.55"
                  />
                )}
                <circle cx={m.x} cy={m.y} r="4.2" fill={palette.markerGlow} />
                <circle cx={m.x} cy={m.y} r="2.2" fill={palette.marker} />
                <circle cx={m.x} cy={m.y} r="0.85" fill={isDark ? '#fff' : '#FAF8F5'} />
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={m.labelAnchor}
                  fontSize="7"
                  fontWeight="700"
                  fill={palette.label}
                  style={{ letterSpacing: '0.02em' }}
                >
                  {m.name}
                </text>
                <text
                  x={labelX}
                  y={labelY + 7.5}
                  textAnchor={m.labelAnchor}
                  fontSize="5.5"
                  fill={palette.labelMuted}
                  style={{ letterSpacing: '0.04em' }}
                >
                  {m.sub}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {showLegend && !compact && (
        <div style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,37,64,0.10)'}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          color: palette.labelMuted,
          fontSize: 10.5,
          lineHeight: 1.5,
        }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <LegendDot color={palette.origin} label="Origin · Tipperary" />
            <LegendDot color={palette.marker} label="Export market" />
            <span style={{ opacity: 0.5 }}>· · ·</span>
            <span>Hairline = production → dispatch route</span>
          </div>
          <div style={{ fontStyle: 'italic', opacity: 0.7, fontSize: 10 }}>
            Map data © Al MacDonald / Fritz Lekschas (CC BY-SA 3.0)
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, hint, palette }) => (
  <div>
    <div style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 22, fontWeight: 700, color: palette.title,
      letterSpacing: '-0.02em', lineHeight: 1,
    }}>{value}</div>
    <div style={{
      fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
      fontWeight: 700, color: palette.labelMuted, marginTop: 3,
    }}>{label}</div>
    {hint && (
      <div style={{ fontSize: 9, color: palette.labelMuted, fontStyle: 'italic', marginTop: 2 }}>
        {hint}
      </div>
    )}
  </div>
);

const LegendDot = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      width: 8, height: 8, borderRadius: '50%', background: color,
      boxShadow: `0 0 0 3px ${color}30`,
    }} />
    <span>{label}</span>
  </span>
);
